import { Component, computed, effect, inject, model, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { LeadService } from '../../../core/services/lead.service';
import { IntakeAnswer } from '../../../core/models/lead.model';
import { PropertyIntakeModalService } from '../../../core/services/property-intake-modal.service';
import { ContactPropertyStepComponent } from './steps/contact-property-step/contact-property-step.component';
import { ProjectPurposeStepComponent } from './steps/project-purpose-step/project-purpose-step.component';
import { IntendedUseStepComponent } from './steps/intended-use-step/intended-use-step.component';
import { UploadsStepComponent } from './steps/uploads-step/uploads-step.component';
import { ReviewStepComponent } from './steps/review-step/review-step.component';
import { SubmitErrorDialogComponent } from './dialogs/submit-error-dialog/submit-error-dialog.component';
import { SubmitSuccessDialogComponent } from './dialogs/submit-success-dialog/submit-success-dialog.component';
import {
  createEmptyUploads,
  PropertyIntakeUploads,
  resolveCountyOption,
  resolveStateOption,
} from './models/property-intake.model';
import { LandRightsStepComponent } from './steps/land-rights-step/land-rights-step.component';
import { PrimengImports } from '../../../shared/primeng/primeng.imports';

@Component({
  selector: 'krc-property-intake',
  standalone: true,
  imports: [
    PrimengImports,
    ContactPropertyStepComponent,
    IntendedUseStepComponent,
    // UploadsStepComponent,
    ReviewStepComponent,
    SubmitErrorDialogComponent,
    SubmitSuccessDialogComponent,
    // LandRightsStepComponent,
  ],
  templateUrl: './property-intake.component.html',
  styleUrl: './property-intake.component.scss',
})
export class PropertyIntakeComponent {
  private readonly fb = inject(FormBuilder);
  private readonly leadService = inject(LeadService);
  private readonly intakeModal = inject(PropertyIntakeModalService);

  readonly visible = model(false);
  readonly confirming = signal(false);

  // Single data-entry screen (contact + property + ADU purpose, all at once) followed
  // by a review screen — replaces the old multi-step wizard (contact → purpose → review).
  readonly totalSteps = 2;
  readonly currentStep = signal(1);
  readonly progressPercent = computed(() => (this.currentStep() / this.totalSteps) * 100);

  readonly stepTitles: Record<number, string> = {
    1: 'Contact & Property Information',
    2: 'Review Your Information',
  };

  readonly isSaving = signal(false);
  readonly apiError = signal<string | null>(null);
  readonly submitted = signal(false);

  readonly errorDialogVisible = signal(false);
  readonly successDialogVisible = signal(false);
  // Set right before hiding the main dialog to show a result dialog on top of it, so
  // the (onHide) handler below doesn't mistake that programmatic hide for the user
  // dismissing the form (X button/ESC/mask click) and reset everything.
  private suppressMainDialogHide = false;

  // Populated once Step 1 successfully creates lead + homeowner + property.
  private readonly leadId = signal<string | null>(null);
  private readonly propertyId = signal<string | null>(null);

  readonly contactPropertyForm = this.fb.nonNullable.group({
    fullName: ['', [Validators.required, Validators.pattern(/^[A-Za-z]+(?:[\s'-][A-Za-z]+)*$/)]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.pattern(/^[0-9()+\-\s]{7,15}$/)]],
    preferredContact: ['email', Validators.required],
    unit: ['', Validators.pattern(/^[A-Za-z0-9\s#'-]*$/)],
    streetAddress: ['', Validators.required],
    zipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    city: ['', [Validators.required, Validators.pattern(/^[A-Za-z\s'-]+$/)]],
    state: ['CA', Validators.required],
    county: ['LOS_ANGELES', Validators.required],
    // Raw device GPS reading, populated only by "Current Location" — not shown in the
    // template. The backend reverse-geocodes these asynchronously for the canonical
    // address/place metadata; see contact-property-step.component.ts's useCurrentLocation.
    latitude: this.fb.control<number | null>(null),
    longitude: this.fb.control<number | null>(null),
    isOwner: ['yes', Validators.required],
    propertyType: [''],
    buildTimeline: ['', Validators.required],
  });

  readonly projectPurposeForm = this.fb.nonNullable.group({
    timeline: ['', Validators.required],
    budget: [''],
    currentStage: [''],
    hasExistingStructure: ['no'],
  });

  readonly intendedUseForm = this.fb.nonNullable.group({
    currentStage: [''],
    intendedUse: this.fb.nonNullable.control<string[]>([], Validators.required),
    intendedUseOtherDescription: ['', Validators.maxLength(300)],
    primaryInterest: this.fb.nonNullable.control<string[]>([]),
    aboutAdu: [''],
    needsFinancing: [''],
    hasCurrentMortgage: [''],
    notes: ['', Validators.maxLength(500)],
  });

  readonly landRightsForm = this.fb.nonNullable.group({
    awareOfEasements: ['', Validators.required],
    easementsDescription: ['', Validators.maxLength(500)],
    inWuiZone: ['', Validators.required],
    impactedByWildfires: ['', Validators.required],
    wildfire: [''],
    isRebuilding: [''],
    femaCleanup: [''],
    pursuingDebrisRemoval: [''],
    applicationNo: [''],
  });

  readonly consentForm = this.fb.nonNullable.group({
    infoAccurate: [false, Validators.requiredTrue],
    agreeTerms: [false, Validators.requiredTrue],
    agreeContact: [false, Validators.requiredTrue],
  });

  readonly uploads = signal<PropertyIntakeUploads>(createEmptyUploads());

  constructor() {
    // Prefills Step 1 with the address the user already picked on the landing
    // page's address autocomplete before opening this modal. Consumed once
    // (cleared right away) so it doesn't re-apply on a later open with no prefill.
    effect(() => {
      if (!this.visible()) {
        return;
      }
      const prefill = this.intakeModal.prefillAddress();
      if (!prefill) {
        return;
      }
      this.intakeModal.prefillAddress.set(null);
      const current = this.contactPropertyForm.getRawValue();
      this.contactPropertyForm.patchValue({
        streetAddress: prefill.streetAddress || current.streetAddress,
        unit: prefill.unit || current.unit,
        city: prefill.city || current.city,
        state: prefill.state ? resolveStateOption(prefill.state) : current.state,
        county: prefill.county ? (resolveCountyOption(prefill.county) ?? current.county) : current.county,
        zipCode: prefill.zipCode || current.zipCode,
        latitude: prefill.latitude ?? current.latitude,
        longitude: prefill.longitude ?? current.longitude,
      });
    });
  }

  async goNext(): Promise<void> {
    // Step 1 now holds both the contact/property fields and the ADU-purpose fields
    // on one screen, so both groups have to pass before moving on to Review.
    if (this.currentStep() === 1 && (this.contactPropertyForm.invalid || this.intendedUseForm.invalid)) {
      this.contactPropertyForm.markAllAsTouched();
      this.intendedUseForm.markAllAsTouched();
      return;
    }

    this.apiError.set(null);
    this.isSaving.set(true);
    try {
      await this.persistStep(this.currentStep());
      if (this.currentStep() < this.totalSteps) {
        this.currentStep.update(step => step + 1);
      }
    } catch (error) {
      this.apiError.set(this.extractErrorMessage(error));
      this.hideMainDialog();
      this.errorDialogVisible.set(true);
    } finally {
      this.isSaving.set(false);
    }
  }

  goPrevious(): void {
    this.apiError.set(null);
    if (this.currentStep() > 1) {
      this.currentStep.update(step => step - 1);
    }
  }

  goToStep(step: number): void {
    this.apiError.set(null);
    this.currentStep.set(step);
  }

  retry(): void {
    this.errorDialogVisible.set(false);
    this.apiError.set(null);
    this.showMainDialog();
    if (this.currentStep() < this.totalSteps) {
      this.goNext();
    } else {
      this.submit();
    }
  }

  onMainDialogHide(): void {
    if (this.suppressMainDialogHide) {
      this.suppressMainDialogHide = false;
      return;
    }
    this.close();
  }

  private hideMainDialog(): void {
    this.suppressMainDialogHide = true;
    this.visible.set(false);
  }

  private showMainDialog(): void {
    this.visible.set(true);
  }

  close(): void {
    this.visible.set(false);
    this.intakeModal.notifyAddressCleared();
    this.errorDialogVisible.set(false);
    this.successDialogVisible.set(false);
    this.currentStep.set(1);
    this.apiError.set(null);
    this.isSaving.set(false);
    this.submitted.set(false);
    this.confirming.set(false);
    this.leadId.set(null);
    this.propertyId.set(null);
    this.uploads.set(createEmptyUploads());

    this.contactPropertyForm.reset({
      fullName: '',
      email: '',
      phone: '',
      preferredContact: 'email',
      unit: '',
      streetAddress: '',
      zipCode: '',
      city: '',
      state: 'CA',
      county: 'LOS_ANGELES',
      latitude: null,
      longitude: null,
      isOwner: 'yes',
      propertyType: '',
      buildTimeline: '',
    });
    this.projectPurposeForm.reset({
      timeline: '',
      budget: '',
      currentStage: '',
      hasExistingStructure: 'no',
    });
    this.intendedUseForm.reset({
      currentStage: '',
      intendedUse: [],
      intendedUseOtherDescription: '',
      primaryInterest: [],
      aboutAdu: '',
      needsFinancing: '',
      hasCurrentMortgage: '',
      notes: '',
    });
    this.landRightsForm.reset({
      awareOfEasements: '',
      easementsDescription: '',
      inWuiZone: '',
      impactedByWildfires: '',
      wildfire: '',
      isRebuilding: '',
      femaCleanup: '',
      pursuingDebrisRemoval: '',
      applicationNo: '',
    });
    this.consentForm.reset({
      infoAccurate: false,
      agreeTerms: false,
      agreeContact: false,
    });
  }

  contactKore(): void {
    this.close();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  }

  onUploadsChange(uploads: PropertyIntakeUploads): void {
    this.uploads.set(uploads);
  }

  confirmSubmit(): void {
    if (this.consentForm.invalid) {
      this.consentForm.markAllAsTouched();
      return;
    }
    this.confirming.set(true);
  }

  cancelConfirm(): void {
    this.confirming.set(false);
  }

  async submit(): Promise<void> {
    if (this.consentForm.invalid) {
      this.consentForm.markAllAsTouched();
      return;
    }
    const ids = this.leadId() && this.propertyId()
      ? { leadId: this.leadId()!, propertyId: this.propertyId()! }
      : null;
    if (!ids) {
      this.apiError.set('Session expired. Please start over.');
      this.hideMainDialog();
      this.errorDialogVisible.set(true);
      return;
    }
    this.confirming.set(false);
    this.apiError.set(null);
    this.isSaving.set(true);
    try {
      await this.sendSubmissionNotification();
      this.submitted.set(true);
      this.hideMainDialog();
      this.successDialogVisible.set(true);
    } catch (error) {
      this.apiError.set(this.extractErrorMessage(error));
      this.hideMainDialog();
      this.errorDialogVisible.set(true);
    } finally {
      this.isSaving.set(false);
    }
  }

  private async sendSubmissionNotification(): Promise<void> {
    const ids = this.requireIds();
    const c = this.contactPropertyForm.getRawValue();

    await firstValueFrom(
      this.leadService.notifyHomeowner({
        leadId: ids.leadId,
        propertyId: ids.propertyId,
        email: c.email,
      }),
    );
  }

  private persistStep(step: number): Promise<void> {
    switch (step) {
      // Both API calls happen together now that contact/property and ADU-purpose
      // are collected on the same screen — saveIntendedUse needs the lead/property
      // ids startJourney creates, so it has to run right after, not on its own step.
      case 1: return this.startJourneyAndSaveIntendedUse();
      default: return Promise.resolve();
    }
  }

  private async startJourneyAndSaveIntendedUse(): Promise<void> {
    await this.startJourney();
    await this.saveIntendedUse();
  }

  // STEP 1 — single API call creates homeowner + property + lead
  private async startJourney(): Promise<void> {
    if (this.propertyId()) return;

    const c = this.contactPropertyForm.getRawValue();
    const [firstName, ...rest] = c.fullName.trim().split(/\s+/);

    const res = await firstValueFrom(
      this.leadService.start({
        firstName,
        lastName: rest.join(' ') || undefined,
        email: c.email,
        phone: c.phone || undefined,
        streetAddress1: c.streetAddress,
        streetAddress2: c.unit || undefined,
        city: c.city,
        state: c.state,
        county: c.county,
        zipCode: c.zipCode,
        latitude: c.latitude ?? undefined,
        longitude: c.longitude ?? undefined,
        ownerStatus: c.isOwner === 'yes' ? 'OWNER' : 'OTHER',
        propertyType: c.propertyType || undefined,
        source: 'homeowner-property-intake',
        marketingConsent: false,
        preferredContact: c.preferredContact || undefined,
        buildTimeline: c.buildTimeline || undefined,
      }),
    );

    this.leadId.set(res.leadId);
    this.propertyId.set(res.propertyId);
  }

  // STEP 2 — save timeline, budget, stage + preferred contact from step 1
  private async saveProjectPurpose(): Promise<void> {
    const ids = this.requireIds();
    const p = this.projectPurposeForm.getRawValue();
    const c = this.contactPropertyForm.getRawValue();

    const answers: IntakeAnswer[] = [];
    if (p.timeline) answers.push({ questionCode: 'INTAKE_TIMELINE', value: p.timeline });
    if (p.budget) answers.push({ questionCode: 'INTAKE_BUDGET', value: p.budget });
    if (p.currentStage) answers.push({ questionCode: 'INTAKE_STAGE', value: p.currentStage });
    if (c.preferredContact) answers.push({ questionCode: 'INTAKE_CONTACT_METHOD', value: c.preferredContact });
    if (!answers.length) return;

    await firstValueFrom(
      this.leadService.submitIntakeAnswers({ leadId: ids.leadId, propertyId: ids.propertyId, answers }),
    );
  }

  // STEP 3 — save intended use, primary interest, notes (upsert merges with step 2).
  // Answers are keyed by the real FeasibilityQuestion.questionCode they're for —
  // no more hasCurrentMortgage-copies-needsFinancing style bugs from a fixed,
  // hand-maintained field list; only questions with a real value get sent at all.
  private async saveIntendedUse(): Promise<void> {
    const ids = this.requireIds();
    const u = this.intendedUseForm.getRawValue();

    const answers: IntakeAnswer[] = [];
    if (u.currentStage) answers.push({ questionCode: 'INTAKE_STAGE', value: u.currentStage });
    if (u.intendedUse.length) answers.push({ questionCode: 'INTAKE_INTENDED_USE', value: u.intendedUse });
    if (u.intendedUse.includes('other') && u.intendedUseOtherDescription) {
      answers.push({ questionCode: 'INTAKE_INTENDED_USE_OTHER', value: u.intendedUseOtherDescription });
    }
    if (u.primaryInterest.length) answers.push({ questionCode: 'INTAKE_PRIMARY_INTEREST', value: u.primaryInterest });
    if (u.aboutAdu) answers.push({ questionCode: 'INTAKE_ABOUT_ADU', value: u.aboutAdu });
    if (u.needsFinancing) answers.push({ questionCode: 'INTAKE_NEEDS_FINANCING', value: u.needsFinancing });
    if (u.notes) answers.push({ questionCode: 'INTAKE_NOTES', value: u.notes });

    if (!answers.length) return;

    await firstValueFrom(
      this.leadService.submitIntakeAnswers({ leadId: ids.leadId, propertyId: ids.propertyId, answers }),
    );
  }

  // STEP: Land Rights — was a stub that discarded everything; now reuses the same
  // generic /homeowners/intake endpoint as the other steps, since these are just
  // more FeasibilityQuestion rows (stepKey: LAND_RIGHTS), not a separate concept.
  private async saveLandRights(): Promise<void> {
    const ids = this.requireIds();
    const l = this.landRightsForm.getRawValue();

    const answers: IntakeAnswer[] = [];
    if (l.awareOfEasements) answers.push({ questionCode: 'LAND_AWARE_EASEMENTS', value: l.awareOfEasements });
    if (l.easementsDescription) answers.push({ questionCode: 'LAND_EASEMENTS_DESCRIPTION', value: l.easementsDescription });
    if (l.inWuiZone) answers.push({ questionCode: 'LAND_IN_WUI_ZONE', value: l.inWuiZone });
    if (l.impactedByWildfires) answers.push({ questionCode: 'LAND_IMPACTED_BY_WILDFIRES', value: l.impactedByWildfires });
    if (l.wildfire) answers.push({ questionCode: 'LAND_WILDFIRE_SELECTION', value: l.wildfire });
    if (l.isRebuilding) answers.push({ questionCode: 'LAND_IS_REBUILDING', value: l.isRebuilding });
    if (l.femaCleanup) answers.push({ questionCode: 'LAND_FEMA_CLEANUP', value: l.femaCleanup });
    if (l.pursuingDebrisRemoval) answers.push({ questionCode: 'LAND_PURSUING_DEBRIS_REMOVAL', value: l.pursuingDebrisRemoval });
    if (l.applicationNo) answers.push({ questionCode: 'LAND_APPLICATION_NO', value: l.applicationNo });

    if (!answers.length) return;

    await firstValueFrom(
      this.leadService.submitIntakeAnswers({ leadId: ids.leadId, propertyId: ids.propertyId, answers }),
    );
  }

  // STEP 4 — upload all selected photos and documents in parallel
  private async uploadFiles(): Promise<void> {
    const ids = this.requireIds();
    const uploads = this.uploads();
    const tasks: Promise<unknown>[] = [];

    for (const [slot, file] of Object.entries(uploads.photos)) {
      if (file) {
        tasks.push(firstValueFrom(this.leadService.uploadMedia(ids.propertyId, file, 'PHOTO', slot)));
      }
    }
    for (const [, file] of Object.entries(uploads.documents)) {
      if (file) {
        tasks.push(firstValueFrom(this.leadService.uploadMedia(ids.propertyId, file, 'DOCUMENT')));
      }
    }

    await Promise.all(tasks);
  }

  private requireIds(): { leadId: string; propertyId: string } {
    const leadId = this.leadId();
    const propertyId = this.propertyId();
    if (!leadId || !propertyId) throw new Error('Complete Step 1 before continuing.');
    return { leadId, propertyId };
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'object' && error !== null && 'error' in error) {
      const httpError = (error as { error?: { message?: string } }).error;
      if (httpError?.message) {
        return httpError.message;
      }
    }
    return 'Something went wrong while saving your information. Please try again.';
  }

}
